import { Call, AgendaNode } from '../calls.service'
import { TemplateItem } from '../template-editor.component/types/template.types'

export type MeetingHealthItem = {
  label: string
  isComplete: boolean
  progress?: string
  helpLink?: string
  subtext?: string
}

function hasAnyPresentersAssigned(templateItems: TemplateItem[]): boolean {
  const checkNode = (item: TemplateItem): boolean => {
    const hasContentBlocks = item.contentBlocks && item.contentBlocks.length > 0

    if (hasContentBlocks && item.presenters && item.presenters.length > 0) {
      return true
    }

    if (item.subitems && item.subitems.length > 0) {
      return item.subitems.some(checkNode)
    }

    return false
  }

  return templateItems.some(checkNode)
}

function hasAnyPresentersInAgenda(agenda: AgendaNode[]): boolean {
  const checkNode = (item: AgendaNode): boolean => {
    if (item.presenters && item.presenters.trim().length > 0) {
      return true
    }

    if (item.subitems && item.subitems.length > 0) {
      return item.subitems.some(checkNode)
    }

    return false
  }

  return agenda.some(checkNode)
}

function calculateLabeledSpeechPercentage(call: Call): number {
  if (!call.transcript || call.transcript.length === 0) {
    return 0
  }

  const speakerDurations = call.transcript
    .filter((slot) => slot.type === 'speaker' && slot.sentences && slot.sentences.length > 0)
    .reduce((durations, slot) => {
      if (slot.type !== 'speaker') {
        return durations
      }

      const speakerTag = slot.speaker.speakerTag
      const firstSentence = slot.sentences[0]
      const lastSentence = slot.sentences[slot.sentences.length - 1]
      const duration = lastSentence.endTimeSeconds - firstSentence.startTimeSeconds

      const currentDuration = durations.get(speakerTag) || 0
      durations.set(speakerTag, currentDuration + duration)
      return durations
    }, new Map<string, number>())

  const { totalDuration, labeledDuration } = Array.from(speakerDurations.entries()).reduce(
    (acc, [speakerTag, duration]) => {
      const mappedName = call.userSpeakerMapping?.[speakerTag]
      const isLabeled = mappedName && mappedName !== speakerTag

      return {
        totalDuration: acc.totalDuration + duration,
        labeledDuration: acc.labeledDuration + (isLabeled ? duration : 0),
      }
    },
    { totalDuration: 0, labeledDuration: 0 }
  )

  if (totalDuration === 0) {
    return 0
  }

  return Math.round((labeledDuration / totalDuration) * 100)
}

export function getMeetingHealthItems(
  call: Call,
  templateItems: TemplateItem[],
  translateFn: (key: string, params?: Record<string, unknown>) => string
): MeetingHealthItem[] {
  const labeledSpeechPercentage = calculateLabeledSpeechPercentage(call)

  const useTemplateEditor = call.effectivelyUseTemplateForMinuting
  const hasAgenda = useTemplateEditor ? templateItems.length > 0 : call.agenda.length > 0
  const hasPresenters = useTemplateEditor ? hasAnyPresentersAssigned(templateItems) : hasAnyPresentersInAgenda(call.agenda)

  return [
    {
      label: 'CALLS.MEETING_HEALTH.ITEMS.AGENDA_UPLOADED',
      isComplete: hasAgenda,
      helpLink: 'https://help.wiseworks.ai/en/articles/527874-how-to-upload-an-agenda-post-meeting',
      subtext: 'CALLS.MEETING_HEALTH.ITEMS.AGENDA_UPLOADED_SUBTEXT',
    },
    {
      label: 'CALLS.MEETING_HEALTH.ITEMS.ATTENDEE_LIST',
      isComplete: call.attendeeNames.length >= 3,
      helpLink: 'https://help.wiseworks.ai/en/articles/527904-including-an-attendee-list-in-your-agenda',
      subtext: 'CALLS.MEETING_HEALTH.ITEMS.ATTENDEE_LIST_SUBTEXT',
    },
    {
      label: 'CALLS.MEETING_HEALTH.ITEMS.PRESENTERS_ASSIGNED',
      isComplete: hasPresenters,
      helpLink: 'https://help.wiseworks.ai/en/articles/527907-assigning-presenters-to-agenda-items',
      subtext: 'CALLS.MEETING_HEALTH.ITEMS.PRESENTERS_ASSIGNED_SUBTEXT',
    },
    {
      label: 'CALLS.MEETING_HEALTH.ITEMS.SPEAKERS_LABELED',
      isComplete: labeledSpeechPercentage >= 85,
      progress:
        labeledSpeechPercentage < 85
          ? translateFn('CALLS.MEETING_HEALTH.ITEMS.SPEAKERS_LABELED_PROGRESS', {
              percentage: labeledSpeechPercentage,
            })
          : undefined,
      helpLink: 'https://help.wiseworks.ai/en/articles/471812-speaker-labelling',
      subtext: 'CALLS.MEETING_HEALTH.ITEMS.SPEAKERS_LABELED_SUBTEXT',
    },
  ]
}
